/**
 * KYC/AML Egenerklæringsskjema
 * I henhold til hvitvaskingsloven
 */

import React, { useState } from 'react';
import './KYCSkjema.css';

export default function KYCSkjema({ klient, onFullført, onAvbryt }) {
  const [formData, setFormData] = useState({
    // Identifikasjon
    fullt_navn: klient?.navn || '',
    fødselsnummer: '',
    adresse: klient?.adresse || '',
    statsborgerskap: 'Norge',
    id_type: 'pass',
    id_nummer: '',
    id_utstedt_av: '',
    id_gyldig_til: '',

    // Selskap (hvis relevant)
    er_selskap: false,
    org_navn: '',
    org_nummer: klient?.org_nr || '',
    org_form: 'AS',
    styreleder: '',
    daglig_leder: '',

    // Reell rettighetshaver
    er_selv_reell_rettighetshaver: true,
    reell_rettighetshaver_navn: '',
    reell_rettighetshaver_fnr: '',
    eierandel: '',

    // PEP
    er_pep: false,
    er_nær_pep: false,
    pep_posisjon: '',
    pep_land: '',
    pep_periode: '',

    // Sanksjoner
    under_sanksjoner: false,
    statsborger_sanksjonert_land: false,
    bodd_høyrisikoland: false,
    sanksjoner_detaljer: '',

    // Oppdrag
    sak_gjelder: '',
    oppdrag_formål: '',
    oppdrag_varighet: '',
    oppdrag_beløp: '',

    // Midler
    midler_opprinnelse: [],
    midler_annet: '',

    // Bekreftelser
    bekrefter_korrekt: false,
    bekrefter_ikke_kriminelt: false,
    bekrefter_ikke_hvitvasking: false,
    bekrefter_informere_endringer: false,
    bekrefter_lest_retningslinjer: false
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMidlerChange = (option) => {
    setFormData(prev => {
      const midler = prev.midler_opprinnelse.includes(option)
        ? prev.midler_opprinnelse.filter(m => m !== option)
        : [...prev.midler_opprinnelse, option];
      return { ...prev, midler_opprinnelse: midler };
    });
  };

  const kanSende = () => {
    return (
      formData.bekrefter_korrekt &&
      formData.bekrefter_ikke_kriminelt &&
      formData.bekrefter_ikke_hvitvasking &&
      formData.bekrefter_informere_endringer &&
      formData.bekrefter_lest_retningslinjer &&
      formData.fødselsnummer.length >= 11 &&
      formData.id_nummer.length > 0
    );
  };

  const handleSend = async () => {
    if (!kanSende()) {
      alert('Vennligst fyll ut alle obligatoriske felt og bekreft erklæringene');
      return;
    }

    // Legg til IP og tidsstempel
    const submission = {
      ...formData,
      signert_dato: new Date().toISOString(),
      ip_adresse: 'Hentes fra server', // Server må hente faktisk IP
      klient_id: klient.id
    };

    onFullført(submission);
  };

  return (
    <div className="kyc-skjema-container">
      <div className="kyc-header">
        <h2>⚖️ Kundekontroll (KYC/AML)</h2>
        <p>I henhold til hvitvaskingsloven må vi gjennomføre kundekontroll før vi kan påta oss oppdraget.</p>
      </div>

      <div className="kyc-content">
        {/* 1. IDENTIFIKASJON */}
        <section className="kyc-section">
          <h3>1. Identifikasjon</h3>

          <div className="form-group">
            <label>Fullt navn *</label>
            <input
              type="text"
              value={formData.fullt_navn}
              onChange={(e) => handleChange('fullt_navn', e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>Fødselsnummer / D-nummer *</label>
            <input
              type="text"
              value={formData.fødselsnummer}
              onChange={(e) => handleChange('fødselsnummer', e.target.value)}
              placeholder="11 siffer"
              maxLength="11"
              required
            />
          </div>

          <div className="form-group">
            <label>Adresse *</label>
            <input
              type="text"
              value={formData.adresse}
              onChange={(e) => handleChange('adresse', e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>Statsborgerskap</label>
            <input
              type="text"
              value={formData.statsborgerskap}
              onChange={(e) => handleChange('statsborgerskap', e.target.value)}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>ID-dokument type *</label>
              <select
                value={formData.id_type}
                onChange={(e) => handleChange('id_type', e.target.value)}
              >
                <option value="pass">Pass</option>
                <option value="førerkort">Førerkort</option>
                <option value="bankkort">Bankkort med BankID</option>
                <option value="annet">Annet</option>
              </select>
            </div>

            <div className="form-group">
              <label>ID-nummer *</label>
              <input
                type="text"
                value={formData.id_nummer}
                onChange={(e) => handleChange('id_nummer', e.target.value)}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Utstedt av</label>
              <input
                type="text"
                value={formData.id_utstedt_av}
                onChange={(e) => handleChange('id_utstedt_av', e.target.value)}
                placeholder="F.eks. Politiet"
              />
            </div>

            <div className="form-group">
              <label>Gyldig til</label>
              <input
                type="date"
                value={formData.id_gyldig_til}
                onChange={(e) => handleChange('id_gyldig_til', e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* 2. SELSKAP */}
        <section className="kyc-section">
          <h3>2. Selskap / Organisasjon (hvis relevant)</h3>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.er_selskap}
                onChange={(e) => handleChange('er_selskap', e.target.checked)}
              />
              Dette gjelder et selskap/organisasjon
            </label>
          </div>

          {formData.er_selskap && (
            <>
              <div className="form-group">
                <label>Organisasjonsnavn</label>
                <input
                  type="text"
                  value={formData.org_navn}
                  onChange={(e) => handleChange('org_navn', e.target.value)}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Organisasjonsnummer</label>
                  <input
                    type="text"
                    value={formData.org_nummer}
                    onChange={(e) => handleChange('org_nummer', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Organisasjonsform</label>
                  <select
                    value={formData.org_form}
                    onChange={(e) => handleChange('org_form', e.target.value)}
                  >
                    <option value="AS">AS</option>
                    <option value="ASA">ASA</option>
                    <option value="NUF">NUF</option>
                    <option value="ANS">ANS</option>
                    <option value="BA">BA</option>
                    <option value="SA">SA</option>
                    <option value="Annet">Annet</option>
                  </select>
                </div>
              </div>
            </>
          )}
        </section>

        {/* 3. REELL RETTIGHETSHAVER */}
        <section className="kyc-section">
          <h3>3. Reell rettighetshaver</h3>
          <p className="help-text">
            Reell rettighetshaver er fysisk person som i siste instans eier eller kontrollerer
            kunden (direkte/indirekte eierskap &gt;25%, eller kontroll på annen måte).
          </p>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.er_selv_reell_rettighetshaver}
                onChange={(e) => handleChange('er_selv_reell_rettighetshaver', e.target.checked)}
              />
              Jeg er selv reell rettighetshaver
            </label>
          </div>

          {!formData.er_selv_reell_rettighetshaver && (
            <>
              <div className="form-group">
                <label>Reell rettighetshaver - Navn *</label>
                <input
                  type="text"
                  value={formData.reell_rettighetshaver_navn}
                  onChange={(e) => handleChange('reell_rettighetshaver_navn', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Reell rettighetshaver - Fødselsnummer *</label>
                <input
                  type="text"
                  value={formData.reell_rettighetshaver_fnr}
                  onChange={(e) => handleChange('reell_rettighetshaver_fnr', e.target.value)}
                  maxLength="11"
                  required
                />
              </div>

              <div className="form-group">
                <label>Eierandel (%)</label>
                <input
                  type="number"
                  value={formData.eierandel}
                  onChange={(e) => handleChange('eierandel', e.target.value)}
                  min="0"
                  max="100"
                />
              </div>
            </>
          )}
        </section>

        {/* 4. PEP */}
        <section className="kyc-section">
          <h3>4. Politisk eksponert person (PEP)</h3>
          <p className="help-text">
            PEP: Statsoverhode, minister, parlamentsmedlem, høyesterettsdommer, høytstående
            militær offiser, styremedlem i statseide selskaper, ledende i internasjonale organisasjoner.
          </p>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.er_pep}
                onChange={(e) => handleChange('er_pep', e.target.checked)}
              />
              Jeg er, eller har vært, politisk eksponert person (PEP)
            </label>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.er_nær_pep}
                onChange={(e) => handleChange('er_nær_pep', e.target.checked)}
              />
              Jeg er nært familiemedlem eller kjent medarbeider av en PEP
            </label>
          </div>

          {(formData.er_pep || formData.er_nær_pep) && (
            <>
              <div className="form-group">
                <label>Posisjon/rolle</label>
                <input
                  type="text"
                  value={formData.pep_posisjon}
                  onChange={(e) => handleChange('pep_posisjon', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Land</label>
                <input
                  type="text"
                  value={formData.pep_land}
                  onChange={(e) => handleChange('pep_land', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Periode</label>
                <input
                  type="text"
                  value={formData.pep_periode}
                  onChange={(e) => handleChange('pep_periode', e.target.value)}
                  placeholder="F.eks. 2015-2020"
                />
              </div>
            </>
          )}
        </section>

        {/* 5. SANKSJONER */}
        <section className="kyc-section">
          <h3>5. Sanksjoner</h3>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.under_sanksjoner}
                onChange={(e) => handleChange('under_sanksjoner', e.target.checked)}
              />
              Jeg eller min virksomhet er underlagt internasjonale sanksjoner
            </label>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.statsborger_sanksjonert_land}
                onChange={(e) => handleChange('statsborger_sanksjonert_land', e.target.checked)}
              />
              Jeg er statsborger av land under sanksjoner
            </label>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.bodd_høyrisikoland}
                onChange={(e) => handleChange('bodd_høyrisikoland', e.target.checked)}
              />
              Jeg har bodd/arbeidet i høyrisikoland siste 5 år
            </label>
          </div>

          {(formData.under_sanksjoner || formData.statsborger_sanksjonert_land || formData.bodd_høyrisikoland) && (
            <div className="form-group">
              <label>Detaljer</label>
              <textarea
                value={formData.sanksjoner_detaljer}
                onChange={(e) => handleChange('sanksjoner_detaljer', e.target.value)}
                rows="3"
              />
            </div>
          )}
        </section>

        {/* 6. OPPDRAG */}
        <section className="kyc-section">
          <h3>6. Oppdragets art og formål</h3>

          <div className="form-group">
            <label>Hva gjelder saken?</label>
            <textarea
              value={formData.sak_gjelder}
              onChange={(e) => handleChange('sak_gjelder', e.target.value)}
              rows="2"
            />
          </div>

          <div className="form-group">
            <label>Formål med oppdraget</label>
            <textarea
              value={formData.oppdrag_formål}
              onChange={(e) => handleChange('oppdrag_formål', e.target.value)}
              rows="2"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Forventet varighet</label>
              <input
                type="text"
                value={formData.oppdrag_varighet}
                onChange={(e) => handleChange('oppdrag_varighet', e.target.value)}
                placeholder="F.eks. 3-6 måneder"
              />
            </div>

            <div className="form-group">
              <label>Forventet totalbeløp (NOK)</label>
              <input
                type="number"
                value={formData.oppdrag_beløp}
                onChange={(e) => handleChange('oppdrag_beløp', e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* 7. MIDLER */}
        <section className="kyc-section">
          <h3>7. Midlenes opprinnelse</h3>
          <p className="help-text">
            Hvor kommer midlene til å betale advokattjenester fra? (Velg alle som passer)
          </p>

          <div className="checkbox-group">
            {['Lønn/pensjon', 'Salg av eiendom', 'Arv/gave', 'Sparemidler', 'Lån', 'Annet'].map(option => (
              <label key={option}>
                <input
                  type="checkbox"
                  checked={formData.midler_opprinnelse.includes(option)}
                  onChange={() => handleMidlerChange(option)}
                />
                {option}
              </label>
            ))}
          </div>

          {formData.midler_opprinnelse.includes('Annet') && (
            <div className="form-group">
              <label>Spesifiser</label>
              <input
                type="text"
                value={formData.midler_annet}
                onChange={(e) => handleChange('midler_annet', e.target.value)}
              />
            </div>
          )}
        </section>

        {/* 8. BEKREFTELSER */}
        <section className="kyc-section kyc-bekreftelser">
          <h3>8. Bekreftelser *</h3>

          <div className="bekreftelse-gruppe">
            <label>
              <input
                type="checkbox"
                checked={formData.bekrefter_korrekt}
                onChange={(e) => handleChange('bekrefter_korrekt', e.target.checked)}
                required
              />
              <strong>Jeg bekrefter at opplysningene er korrekte</strong>
            </label>

            <label>
              <input
                type="checkbox"
                checked={formData.bekrefter_ikke_kriminelt}
                onChange={(e) => handleChange('bekrefter_ikke_kriminelt', e.target.checked)}
                required
              />
              <strong>Jeg bekrefter at midlene ikke stammer fra kriminell aktivitet</strong>
            </label>

            <label>
              <input
                type="checkbox"
                checked={formData.bekrefter_ikke_hvitvasking}
                onChange={(e) => handleChange('bekrefter_ikke_hvitvasking', e.target.checked)}
                required
              />
              <strong>Jeg bekrefter at jeg ikke deltar i hvitvasking eller terrorfinansiering</strong>
            </label>

            <label>
              <input
                type="checkbox"
                checked={formData.bekrefter_informere_endringer}
                onChange={(e) => handleChange('bekrefter_informere_endringer', e.target.checked)}
                required
              />
              <strong>Jeg forplikter meg til å informere advokaten om endringer i disse opplysningene</strong>
            </label>

            <label>
              <input
                type="checkbox"
                checked={formData.bekrefter_lest_retningslinjer}
                onChange={(e) => handleChange('bekrefter_lest_retningslinjer', e.target.checked)}
                required
              />
              <strong>Jeg har lest og forstått Advokatforeningens retningslinjer for kundekontroll</strong>
            </label>
          </div>
        </section>

        {/* ELEKTRONISK SIGNATUR */}
        <section className="kyc-section kyc-signatur">
          <h3>Elektronisk signatur</h3>
          <p>
            Ved å klikke "Jeg bekrefter" nedenfor, signerer du denne egenerklæringen elektronisk.
            Din IP-adresse og tidsstempel vil bli registrert.
          </p>

          <div className="signatur-info">
            <p><strong>Dato:</strong> {new Date().toLocaleDateString('nb-NO')}</p>
            <p><strong>Tidspunkt:</strong> {new Date().toLocaleTimeString('nb-NO')}</p>
          </div>
        </section>
      </div>

      {/* HANDLINGER */}
      <div className="kyc-actions">
        <button className="btn-secondary" onClick={onAvbryt}>
          Avbryt
        </button>
        <button
          className="btn-primary"
          onClick={handleSend}
          disabled={!kanSende()}
        >
          ✓ Jeg bekrefter og sender inn
        </button>
      </div>
    </div>
  );
}
